import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, User, Loader2, MailCheck } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { findCountry } from "@/lib/countries";
import PhoneField from "@/components/PhoneField";
import LangToggle from "@/components/LangToggle";
import api from "@/lib/api";
import { toast } from "sonner";

const AUTH_BG = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const FIELD = "w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl pl-12 pr-12 py-3.5 text-[var(--rc-text)] placeholder:text-[var(--rc-muted)] focus:outline-none focus:border-[var(--rc-gold)]/60 focus:ring-1 focus:ring-[var(--rc-gold)]/50 transition-colors";


export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const { login, registerPending, finalizeSession } = useAuth();
  const { t } = useI18n();
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [showPw, setShowPw] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [phone, setPhone] = useState({ code: "US", phone: "" });
  const [pendingVerify, setPendingVerify] = useState(false); // email awaiting verification post-signup
  const [vcode, setVcode] = useState("");
  const [vbusy, setVbusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false); // password-reset: code emailed, awaiting entry
  const [rcode, setRcode] = useState("");
  const [rpw, setRpw] = useState("");
  const [cooldown, setCooldown] = useState(0); // seconds left before "Resend" is allowed again
  const [emailTaken, setEmailTaken] = useState(false); // live signup email-availability check

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkEmail = async () => {
    if (isLogin || forgot) return;
    const e = form.email.trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setEmailTaken(false); return; }
    try { const { data } = await api.get("/auth/email-available", { params: { email: e } }); setEmailTaken(!data.available); }
    catch (err) { setEmailTaken(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        if (!forgotSent) {
          await api.post("/auth/forgot-password", { email: form.email });
          toast.success(t("reset_code_sent"));
          setForgotSent(true);
          setCooldown(60);
        } else {
          if (rpw.length < 6) { toast.error(t("pw_too_short")); return; }
          await api.post("/auth/reset-password-code", { email: form.email, code: rcode.trim(), new_password: rpw });
          toast.success(t("reset_success"));
          setForgot(false); setForgotSent(false); setRcode(""); setRpw(""); setIsLogin(true);
        }
        return;
      }
      if (isLogin) {
        await login(form.email, form.password);
        navigate("/app");
      } else {
        if (!phone.phone || !phone.phone.trim()) {
          toast.error(t("phone_required"));
          return;
        }
        if (emailTaken) { toast.error(t("email_taken")); return; }
        const c = findCountry(phone.code);
        await registerPending({
          name: form.name, email: form.email, password: form.password,
          country: c.name, flag: c.flag, phone: `${c.dial}${phone.phone}`,
        });
        // Nothing is saved to the account DB yet — the email must be verified first.
        setPendingVerify(true);
        toast.success(t("verify_code_sent"));
        setCooldown(60);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendVerify = async () => {
    if (cooldown > 0) return;
    setVbusy(true);
    try { await api.post("/auth/resend-registration-code", { email: form.email }); toast.success(t("verify_code_sent")); setCooldown(60); }
    catch (e) { toast.error(t("verify_send_failed")); }
    finally { setVbusy(false); }
  };

  const confirmVerify = async () => {
    if (vcode.trim().length < 4) return;
    setVbusy(true);
    try {
      const { data } = await api.post("/auth/verify-registration", { email: form.email, code: vcode.trim() });
      toast.success(t("verify_success"));
      finalizeSession(data.token, data.user);
      navigate("/app");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("verify_invalid")); }
    finally { setVbusy(false); }
  };

  const cancelVerify = () => {
    setPendingVerify(false); setVcode("");
  };

  const switchMode = () => { setIsLogin((v) => !v); setForgot(false); setForgotSent(false); setRcode(""); setRpw(""); };

  return (
    <div className="relative min-h-screen bg-[var(--rc-bg)] text-[var(--rc-text)] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={AUTH_BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-[var(--rc-bg)]/80" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.14), transparent 60%)" }} />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 pt-6">
          {!isLogin && !forgot ? (
            <button onClick={switchMode} className="h-10 w-10 grid place-items-center rounded-full text-[var(--rc-silver)] hover:text-[var(--rc-gold)]" data-testid="auth-back-btn">
              <ArrowLeft size={24} />
            </button>
          ) : forgot ? (
            <button onClick={() => { setForgot(false); setForgotSent(false); setRcode(""); setRpw(""); }} className="h-10 w-10 grid place-items-center rounded-full text-[var(--rc-silver)] hover:text-[var(--rc-gold)]" data-testid="auth-back-btn">
              <ArrowLeft size={24} />
            </button>
          ) : <span />}
          <LangToggle />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="flex-1 flex flex-col justify-start px-6 pt-4 pb-10 w-full max-w-md mx-auto"
        >
          {pendingVerify ? (
            <div className="flex flex-col items-center text-center mt-8" data-testid="signup-verify-step">
              <div className="h-20 w-20 rounded-full grid place-items-center bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] mb-5">
                <MailCheck size={38} />
              </div>
              <h1 className="font-heading text-4xl text-[var(--rc-gold)] mb-2" data-testid="verify-title">{t("verify_email")}</h1>
              <p className="text-sm text-[var(--rc-muted)] max-w-sm mb-1">{t("signup_verify_intro")}</p>
              <p className="text-sm font-semibold text-[var(--rc-text)] mb-6">{form.email}</p>
              <input value={vcode} onChange={(e) => setVcode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000" data-testid="signup-verify-code-input" autoFocus
                className="w-full text-center tracking-[0.5em] text-2xl font-semibold bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[var(--rc-gold)]/60" />
              <button onClick={confirmVerify} disabled={vbusy || vcode.length < 4} data-testid="signup-verify-confirm-btn"
                className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-4 text-lg disabled:opacity-50">
                {vbusy && <Loader2 size={18} className="animate-spin" />} {t("verify_confirm")}
              </button>
              <button onClick={resendVerify} disabled={vbusy || cooldown > 0} data-testid="signup-verify-resend-btn"
                className="mt-4 text-sm text-[var(--rc-gold)] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed">
                {cooldown > 0 ? t("resend_in").replace("{s}", cooldown) : t("verify_resend")}
              </button>
              <button onClick={cancelVerify} data-testid="signup-verify-cancel-btn"
                className="mt-2 text-xs text-[var(--rc-muted)] hover:text-[var(--rc-text)]">{t("verify_use_other_email")}</button>
            </div>
          ) : (<>
          {isLogin && !forgot ? (
            <div className="text-center mb-8 mt-6">
              <div className="text-6xl mb-3">👑</div>
              <h1 className="font-heading text-5xl text-[var(--rc-gold)] tracking-tight" data-testid="auth-title">RoyalChat Nova</h1>
              <p className="text-[var(--rc-muted)] mt-2">{t("login_tagline")}</p>
            </div>
          ) : (
            <div className="mb-8 mt-4">
              <h1 className="font-heading text-5xl sm:text-6xl leading-[1.05] tracking-tight whitespace-pre-line" data-testid="auth-title">
                {forgot ? t("reset_pw_title") : t("create_account_title")}
              </h1>
              <p className="text-[var(--rc-muted)] text-lg mt-4">
                {forgot ? t("reset_pw_desc") : t("create_account_sub")}
              </p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && !forgot && (
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-gold)]" />
                <input required value={form.name} onChange={set("name")} placeholder={t("name")} className={FIELD} data-testid="register-name-input" />
              </div>
            )}

            <div className="relative">
              {isLogin && !forgot
                ? <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-gold)]" />
                : <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-gold)]" />}
              <input required type={isLogin ? "text" : "email"} value={form.email}
                onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); if (emailTaken) setEmailTaken(false); }}
                onBlur={checkEmail} disabled={forgot && forgotSent}
                placeholder={isLogin && !forgot ? t("email_or_phone") : t("email")} className={`${FIELD} disabled:opacity-60`} data-testid="auth-email-input" />
            </div>
            {!isLogin && !forgot && emailTaken && (
              <p className="-mt-2 text-xs text-red-400" data-testid="auth-email-taken">{t("email_taken")}</p>
            )}

            {forgot && forgotSent && (
              <>
                <p className="text-sm text-[var(--rc-muted)]" data-testid="reset-code-hint">{t("reset_code_hint")}</p>
                <input value={rcode} onChange={(e) => setRcode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" placeholder="000000" data-testid="reset-code-input"
                  className="w-full text-center tracking-[0.5em] text-2xl font-semibold bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[var(--rc-gold)]/60" />
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-gold)]" />
                  <input required type={showPw ? "text" : "password"} value={rpw} onChange={(e) => setRpw(e.target.value)} placeholder={t("new_password")} className={FIELD} data-testid="reset-new-password-input" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-reset-password-btn"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--rc-muted)] hover:text-[var(--rc-gold)] transition-colors">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <button type="button" disabled={cooldown > 0}
                  onClick={async () => { if (cooldown > 0) return; try { await api.post("/auth/forgot-password", { email: form.email }); toast.success(t("reset_code_sent")); setCooldown(60); } catch (e) {} }}
                  data-testid="reset-resend-btn" className="text-sm text-[var(--rc-gold)] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed">
                  {cooldown > 0 ? t("resend_in").replace("{s}", cooldown) : t("verify_resend")}
                </button>
              </>
            )}

            {!isLogin && !forgot && (
              <PhoneField value={phone} onChange={setPhone} />
            )}

            {!forgot && (
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-gold)]" />
                <input required type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder={t("password")} className={FIELD} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password-btn"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--rc-muted)] hover:text-[var(--rc-gold)] transition-colors">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            )}

            {isLogin && !forgot && (
              <div className="flex justify-end">
                <button type="button" onClick={() => setForgot(true)} className="text-sm text-[var(--rc-gold)] hover:underline" data-testid="forgot-password-link">{t("forgot_password")}</button>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-4 text-lg hover:bg-[var(--rc-gold-light)] transition-colors disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />}
              {forgot ? (forgotSent ? t("reset_password_cta") : t("send_reset")) : isLogin ? t("login") : t("create_account_cta")}
            </button>
          </form>

          {!forgot && (
            isLogin ? (
              <button onClick={switchMode} type="button" data-testid="switch-auth-mode"
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-full border-2 border-[var(--rc-gold)] text-[var(--rc-gold)] font-semibold py-4 text-lg hover:bg-[var(--rc-gold)] hover:text-black transition-colors">
                {t("create_account_cta")}
              </button>
            ) : (
              <p className="mt-7 text-center text-sm text-[var(--rc-muted)]">
                {t("already_account")}{" "}
                <button onClick={switchMode} className="text-[var(--rc-gold)] font-semibold hover:underline" data-testid="switch-auth-mode">
                  {t("login")}
                </button>
              </p>
            )
          )}
          </>)}
        </motion.div>
      </div>
    </div>
  );
}
